import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useLenis } from 'lenis/react'
import Link from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import ScrollCta from '@/components/base/scroll-cta'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { collectionLandingDesktopRevealAnimation } from '@/components/sections/animations/collection-landing-desktop'
import useStableViewport from '@/hooks/use-stable-viewport'

import { cn } from '@/lib/utils'

export type CollectionLandingDesktopProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    text: string
    items: any[]
  }>

gsap.registerPlugin(ScrollTrigger)

const OPACITY_BY_DISTANCE = [1, 0.55, 0.45, 0.3, 0.2] as const
const CTA_FADE_PROGRESS = 0.2
const LINK_FADE_DURATION = 0.25
const SCROLL_EASE__IN_OUT_CUBIC = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const getOpacityByIndexDistance = (distance: number) =>
  OPACITY_BY_DISTANCE[Math.min(distance, OPACITY_BY_DISTANCE.length - 1)]

const getActiveImageIndex = (
  imageNodes: HTMLElement[],
  viewportHeight: number
) => {
  const viewportCenter = viewportHeight * 0.5
  let activeIndex = 0
  let minDistance = Number.POSITIVE_INFINITY

  imageNodes.forEach((imageNode, index) => {
    const imageRect = imageNode.getBoundingClientRect()
    const imageCenter = imageRect.top + imageRect.height * 0.5
    const distance = Math.abs(imageCenter - viewportCenter)

    if (distance < minDistance) {
      minDistance = distance
      activeIndex = index
    }
  })

  return activeIndex
}

const CollectionLandingDesktop = ({
  title,
  text,
  items,
  className,
  ...props
}: CollectionLandingDesktopProps) => {
  const rootRef = useRef<HTMLDivElement>(null!)
  const stickyRef = useRef<HTMLDivElement>(null)
  const lenis = useLenis()
  const [stickyTop, setStickyTop] = useState<number | null>(null)
  const isShort = useMedia('(max-height: 750px)', false)
  const safeItems = useMemo(() => items || [], [items])
  const { width: viewportWidth, height: viewportHeight } = useStableViewport()

  useEffect(() => {
    const node = stickyRef.current
    if (!node) return

    const rafId = window.requestAnimationFrame(() => {
      setStickyTop(node.getBoundingClientRect().top)
    })

    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [])

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return

      const linksContainer = root.querySelector(
        '[data-collection-landing-links]'
      ) as HTMLElement | null
      const mainTitle = root.querySelector(
        '[data-collection-landing-main-title]'
      ) as HTMLElement | null
      const mainText = root.querySelector(
        '[data-collection-landing-main-text]'
      ) as HTMLElement | null
      const scrollCta = root.querySelector(
        '[data-scroll-cta]'
      ) as HTMLElement | null
      const imageNodes = Array.from(
        root.querySelectorAll('[data-collection-landing-image]')
      ) as HTMLElement[]

      const scrollElement = document.documentElement

      let onLenisScroll: (() => void) | undefined
      let onTick: (() => void) | undefined

      /* if (lenis) {
        ScrollTrigger.scrollerProxy(scrollElement, {
          scrollTop(value) {
            if (typeof value === 'number') {
              lenis.scrollTo(value, { immediate: true, duration: 0 })
            }

            return lenis.scroll
          },
          getBoundingClientRect() {
            return {
              top: 0,
              left: 0,
              width: viewportWidth,
              height: viewportHeight
            }
          },
          pinType: 'transform'
        })

        onLenisScroll = () => ScrollTrigger.update()
        lenis.on('scroll', onLenisScroll)

        onTick = () => ScrollTrigger.update()
        gsap.ticker.add(onTick)
      } */

      const hoverHandlers: Array<{
        node: HTMLAnchorElement
        handler: () => void
      }> = []
      let opacityTrigger: ScrollTrigger | undefined

      if (linksContainer && imageNodes.length > 0) {
        const linkNodes = Array.from(
          linksContainer.querySelectorAll('[data-collection-landing-link]')
        ) as HTMLAnchorElement[]

        const opacityNodes: HTMLElement[] = [
          ...linkNodes,
          ...(mainTitle ? [mainTitle] : []),
          ...(mainText ? [mainText] : []),
          ...(scrollCta ? [scrollCta] : [])
        ]

        opacityNodes.forEach((node) => {
          node.style.willChange = 'opacity'
        })

        const opacitySetters = linkNodes.map((node) =>
          gsap.quickTo(node, 'opacity', {
            duration: LINK_FADE_DURATION,
            ease: 'power2.out'
          })
        )
        const mainTitleOpacity = mainTitle
          ? gsap.quickTo(mainTitle, 'opacity', {
              duration: LINK_FADE_DURATION,
              ease: 'power2.out'
            })
          : null

        const mainTextOpacity = mainText
          ? gsap.quickTo(mainText, 'opacity', {
              duration: LINK_FADE_DURATION,
              ease: 'power2.out'
            })
          : null

        const ctaOpacity = scrollCta
          ? gsap.quickTo(scrollCta, 'opacity', {
              duration: 0.4,
              ease: 'power2.out'
            })
          : null

        const updateLinksOpacity = (progress = 0) => {
          const activeIndex = getActiveImageIndex(imageNodes, viewportHeight)

          linkNodes.forEach((_, index) => {
            const imageNode = imageNodes[index]
            if (!imageNode) {
              opacitySetters[index]?.(
                OPACITY_BY_DISTANCE[OPACITY_BY_DISTANCE.length - 1]
              )
              return
            }

            const distanceByIndex = Math.abs(index - activeIndex)
            const opacity = getOpacityByIndexDistance(distanceByIndex)

            opacitySetters[index](opacity)
          })

          if (mainTitleOpacity) {
            mainTitleOpacity(getOpacityByIndexDistance(Math.abs(activeIndex)))
          }

          if (ctaOpacity) {
            ctaOpacity(progress > CTA_FADE_PROGRESS ? 0 : 1)
          }

          if (mainTextOpacity) {
            mainTextOpacity(progress > CTA_FADE_PROGRESS ? 0 : 1)
          }
        }

        opacityTrigger = ScrollTrigger.create({
          trigger: root,
          scroller: lenis ? scrollElement : undefined,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true,
          onRefresh: (self) => updateLinksOpacity(self.progress),
          onUpdate: (self) => updateLinksOpacity(self.progress)
        })

        requestAnimationFrame(() =>
          updateLinksOpacity(opacityTrigger?.progress || 0)
        )

        linkNodes.forEach((linkNode, index) => {
          const imageNode = imageNodes[index]
          if (!imageNode) return

          const handler = () => {
            const imageRect = imageNode.getBoundingClientRect()
            const absoluteTop =
              imageRect.top + window.scrollY + imageRect.height * 0.5
            const targetScroll = absoluteTop - viewportHeight * 0.5

            if (lenis) {
              lenis.scrollTo(targetScroll, {
                duration: 1.2,
                easing: SCROLL_EASE__IN_OUT_CUBIC
              })
            }

            window.scrollTo({ top: targetScroll, behavior: 'smooth' })
          }

          linkNode.addEventListener('pointerenter', handler)
          hoverHandlers.push({ node: linkNode, handler })
        })
      }

      ScrollTrigger.refresh()

      return () => {
        if (lenis && onLenisScroll) {
          lenis.off('scroll', onLenisScroll)
        }

        if (onTick) {
          gsap.ticker.remove(onTick)
        }

        hoverHandlers.forEach(({ node, handler }) => {
          node.removeEventListener('pointerenter', handler)
        })

        const opacityNodes: HTMLElement[] = [
          ...(linksContainer
            ? (Array.from(
                linksContainer.querySelectorAll(
                  '[data-collection-landing-link]'
                )
              ) as HTMLElement[])
            : []),
          ...(mainTitle ? [mainTitle] : []),
          ...(mainText ? [mainText] : []),
          ...(scrollCta ? [scrollCta] : [])
        ]

        opacityNodes.forEach((node) => {
          node.style.willChange = ''
        })

        opacityTrigger?.kill()
      }
    },
    {
      scope: rootRef,
      dependencies: [lenis, safeItems, viewportWidth, viewportHeight],
      revertOnUpdate: true
    }
  )

  return (
    <Motion animation={collectionLandingDesktopRevealAnimation}>
      {(motionRef) => (
        <div ref={motionRef}>
          <Section
            ref={rootRef}
            className={cn('relative', className)}
            {...props}
          >
            <Container>
              <Grid>
                <div
                  ref={stickyRef}
                  className={cn(
                    'sticky z-10 flex flex-col self-start md:col-span-2 lg:col-span-4'
                  )}
                  style={{
                    top: stickyTop !== null ? `${stickyTop}px` : undefined
                  }}
                  data-collection-landing-sticky
                >
                  <div className="flex w-full flex-col">
                    {title ? (
                      <div
                        className="flex items-end gap-spacing-2xs"
                        data-collection-landing-main-title
                        data-motion-title
                      >
                        <svg
                          width="8"
                          height="2"
                          viewBox="0 0 8 2"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0 1C0 0.447715 0.358172 0 0.8 0H7.2C7.64183 0 8 0.447715 8 1C8 1.55228 7.64183 2 7.2 2H0.8C0.358172 2 0 1.55228 0 1Z"
                          />
                        </svg>

                        <Typography variant="regular" component="h1">
                          {title}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={cn(
                      'mt-spacing-lg flex flex-col gap-spacing-2xs',
                      {
                        'gap-spacing-xs': isShort
                      }
                    )}
                    data-collection-landing-links
                  >
                    {safeItems.map((item, index) => (
                      <Link
                        href={item?.link || '#'}
                        key={index}
                        data-collection-landing-link
                        data-motion-item
                        className="flex place-self-start leading-none"
                        scroll={false}
                      >
                        <Typography
                          variant="h1"
                          component="h3"
                          data-collection-landing-title
                          className={cn(
                            'cursor-pointer transition-opacity duration-200',
                            'md:text-s-h2 lg:text-s-h1',
                            { 'md:text-s-h3 lg:text-s-h2': isShort }
                          )}
                        >
                          {item?.title}
                        </Typography>
                      </Link>
                    ))}
                  </div>
                  <ScrollCta
                    data-scroll-cta
                    className="absolute -bottom-spacing-2xl left-0"
                  />
                </div>
                <div
                  className="md:col-span-4 lg:col-span-8"
                  data-collection-landing-scroll
                >
                  {text ? (
                    <div
                      className="flex"
                      data-collection-landing-main-text
                      data-motion-text
                    >
                      <Typography
                        variant="large"
                        paragraph
                        data-collection-landing-text
                        className="text-regular md:text-large mb-padding-md block pr-[20rem] text-text-primary-light dark:text-text-primary-dark"
                      >
                        {text}
                      </Typography>
                    </div>
                  ) : null}
                  <div className="flex flex-col gap-spacing-2xl">
                    {safeItems.map((item, index) => (
                      <ParallaxImage
                        offset={100}
                        key={index}
                        className="relative aspect-video w-[calc(100%+10rem)]"
                        data-collection-landing-image
                        data-motion-image
                      >
                        <Link
                          href={item?.link || '#'}
                          target={item?.link?.target || '_self'}
                          scroll={false}
                          aria-hidden="true"
                          tabIndex={-1}
                        >
                          <img
                            src={item?.image?.url || ''}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover object-center"
                          />
                        </Link>
                      </ParallaxImage>
                    ))}
                  </div>
                </div>
              </Grid>
            </Container>
            <div className="h-[calc(var(--vh)*20)]" />
          </Section>
        </div>
      )}
    </Motion>
  )
}

export default CollectionLandingDesktop
